/**
 * v4.12.0 — Selection KPI bar.
 *
 * Replaces the standard fleet KPI strip with an aggregate view of the
 * operator's current selection. Designed to feel like a contextual
 * upgrade — not a separate widget. Polishes operational orchestration
 * over plain bulk editing.
 *
 * Renders inline (above the table) when ≥1 row is selected and the
 * backend has returned a selection summary.
 */
import { useTranslation } from "react-i18next";
import { CheckSquare, X } from "lucide-react";

const Stat = ({ value, label, accent = "#cbd5f5", testId }) => (
    <div className="flex flex-col gap-0.5" data-testid={testId}>
        <span
            className="text-xl sm:text-2xl font-semibold leading-none"
            style={{ color: accent, fontFamily: "Manrope, sans-serif" }}
        >
            {value}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
            {label}
        </span>
    </div>
);

export default function SelectionKpiBar({
    count, summary, onClearSelection,
}) {
    const { t } = useTranslation();
    if (!count) return null;
    const s = summary || {};
    const fmt = (n) => (n === undefined || n === null) ? "—" : Number(n).toLocaleString();

    return (
        <div
            data-testid="fleet-selection-kpi-bar"
            className="rounded-xl border px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-5 flex-wrap"
            style={{
                background:
                    "linear-gradient(135deg, rgba(56,189,248,0.10), rgba(15,23,42,0.55))",
                borderColor: "rgba(56,189,248,0.30)",
                boxShadow: "0 0 0 1px rgba(56,189,248,0.08) inset",
            }}
        >
            <div className="flex items-center gap-2 pr-3 border-r border-slate-500/20">
                <CheckSquare size={16} className="text-sky-300" strokeWidth={1.9} />
                <div className="leading-tight">
                    <div
                        className="text-base sm:text-lg font-semibold text-slate-100"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                        data-testid="selection-count"
                    >
                        {t("card_fleet.bulk.n_selected", { n: count })}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-sky-300/80">
                        {t("card_fleet.bulk.command_strip_eyebrow")}
                    </div>
                </div>
            </div>

            <Stat
                value={fmt(s.taps_today)}
                label={t("card_fleet.kpi.taps_today")}
                accent="#22d3ee"
                testId="sel-stat-taps-today"
            />
            <Stat
                value={fmt(s.taps_lifetime)}
                label={t("card_fleet.kpi.total_cards")}
                accent="#60a5fa"
                testId="sel-stat-taps-lifetime"
            />
            {typeof s.avg_rating === "number" ? (
                <Stat
                    value={`${s.avg_rating}★`}
                    label={t("card_fleet.kpi_panel.rs_rating_title")}
                    accent="#fbbf24"
                    testId="sel-stat-rating"
                />
            ) : null}
            <Stat
                value={fmt(s.paused_count)}
                label={t("card_fleet.status.paused")}
                accent={s.paused_count ? "#f59e0b" : "#94a3b8"}
                testId="sel-stat-paused"
            />
            <Stat
                value={fmt(s.scheduled_count)}
                label={t("card_fleet.kpi.scheduled_cards")}
                accent="#a78bfa"
                testId="sel-stat-scheduled"
            />
            <Stat
                value={fmt(s.locations)}
                label={t("card_fleet.kpi.total_locations")}
                accent="#34d399"
                testId="sel-stat-locations"
            />

            <button
                type="button"
                onClick={onClearSelection}
                data-testid="selection-clear-btn"
                className="ml-auto inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white px-2 py-1 rounded hover:bg-white/5 transition-colors"
                title={t("card_fleet.bulk.clear_selection")}
            >
                <X size={12} strokeWidth={2} />
                {t("card_fleet.bulk.clear_selection")}
            </button>
        </div>
    );
}
